import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { dateFormatStore } from './dateFormatStore';

/**
 * Default currency per locale.
 * Used when no explicit currency is passed to the {{value, currency}} format.
 */
const DEFAULT_CURRENCY: Record<string, string> = {
  'en-US': 'EUR',
  'en-AU': 'AUD',
};

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en-US',
    supportedLngs: ['en-US', 'en-AU'],

    // Only preload 'common' on boot; all others load lazily
    ns: ['common'],
    defaultNS: 'common',
    fallbackNS: 'common',

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    interpolation: {
      escapeValue: false, // React already escapes

      /**
       * Custom format function – allows locale-aware formatting inside
       * translation strings via named format tokens.
       *
       * Usage in JSON files:
       *   "salary": "Salary: {{amount, currency}}"
       *   "rate":   "{{rate, currency(EUR)}}/hr"
       *   "count":  "{{total, number}} results"
       *   "posted": "Posted {{date, dateShort}}"
       *   "due":    "Due {{date, dateLong}}"
       *   "at":     "at {{date, time}}"
       *
       * In components:
       *   t('salary', { amount: 85000 })
       *   t('rate',   { rate: 45 })
       *   t('posted', { date: new Date() })
       *
       * Dynamic currency from API data:
       *   t('rate', { amount: 45, formatParams: { amount: { currency: 'AUD' } } })
       */
      format(
        value: unknown,
        format?: string,
        lng?: string,
        options?: Record<string, unknown>,
      ): string {
        const locale = lng || i18n.language || 'en-US';
        const dateLocale = dateFormatStore.get();

        // ── Currency ────────────────────────────────────────────────
        // Priority: formatParams.currency > currency(XXX) in format string > locale default
        //
        // 1. {{amount, currency}}                                                → locale default
        // 2. {{amount, currency(USD)}}                                           → always USD
        // 3. t('key', { formatParams: { amount: { currency: 'AUD' } } })        → AUD from API
        if (format?.startsWith('currency')) {
          const fromOptions = options?.currency as string | undefined;
          const fromFormat = format.match(/^currency\((\w+)\)$/)?.[1];
          const code = fromOptions || fromFormat || DEFAULT_CURRENCY[locale] || 'EUR';
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: code,
          }).format(value as number);
        }

        // ── Number ──────────────────────────────────────────────────
        // {{count, number}}
        if (format === 'number') {
          return new Intl.NumberFormat(locale).format(value as number);
        }

        // ── Date: short (numeric) ───────────────────────────────────
        // en-US → 1/15/2026   |   en-AU → 15/01/2026
        // Uses pref_date_format (dateLocale) independent of translation locale
        if (format === 'dateShort' && value instanceof Date) {
          return new Intl.DateTimeFormat(dateLocale, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          }).format(value);
        }

        // ── Date: long (with month name) ────────────────────────────
        // en-US → January 15, 2026   |   en-AU → 15 January 2026
        if (format === 'dateLong' && value instanceof Date) {
          return new Intl.DateTimeFormat(dateLocale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }).format(value);
        }

        // ── Date: medium ────────────────────────────────────────────
        // en-US → Jan 15, 2026   |   en-AU → 15 Jan 2026
        if (format === 'dateMedium' && value instanceof Date) {
          return new Intl.DateTimeFormat(dateLocale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }).format(value);
        }

        // ── Time ────────────────────────────────────────────────────
        // en-US → 3:30 PM   |   en-AU → 3:30 pm
        if (format === 'time' && value instanceof Date) {
          return new Intl.DateTimeFormat(dateLocale, {
            hour: 'numeric',
            minute: 'numeric',
          }).format(value);
        }

        // ── Fallback: return value as-is ────────────────────────────
        return String(value);
      },
    },

    // Required when not all namespaces are bundled
    partialBundledLanguages: true,

    react: {
      useSuspense: true,
    },
  });

export default i18n;
